import React, { useState, useEffect } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { Settings, Globe, LayoutDashboard, Info, BarChart3 } from 'lucide-react';
import GoogleAdManagerTab from './ads/GoogleAdManagerTab';
import AdPlacementsTab from './ads/AdPlacementsTab';
import AdAnalyticsTab from './ads/AdAnalyticsTab';
import SetupGuideTab from './ads/SetupGuideTab';

const API = process.env.REACT_APP_BACKEND_URL;

const DEFAULT_CONFIG = {
  enabled: false,
  network_code: '',
  ad_units: {
    homepage_banner: '',
    sidebar_rectangle: '',
    in_feed: '',
    footer_banner: '',
  },
};

/**
 * Ad System Configuration page — 4 tabs: GAM config, placements, analytics, setup guide.
 */
const AdSystemConfig = () => {
  const { token } = useAuth();
  const [config, setConfig] = useState(DEFAULT_CONFIG);
  const [placements, setPlacements] = useState([]);
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!token) return;
      try {
        const [configRes, placementsRes, statsRes] = await Promise.all([
          fetch(`${API}/api/admin/ads/config`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          fetch(`${API}/api/admin/ads/placements`),
          fetch(`${API}/api/admin/ads/stats`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);
        if (configRes.ok) {
          const data = await configRes.json();
          setConfig(data.config);
          setLastUpdated(data.last_updated);
        }
        if (placementsRes.ok) {
          const data = await placementsRes.json();
          setPlacements(data.placements || []);
        }
        if (statsRes.ok) {
          const data = await statsRes.json();
          setStats(data.stats || []);
        }
      } catch (error) {
        console.error('Failed to load ad config:', error);
        toast.error('Failed to load configuration');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [token]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API}/api/admin/ads/config`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(config),
      });
      if (response.ok) {
        const data = await response.json();
        setConfig(data.config);
        toast.success('Configuration saved successfully');
      } else {
        throw new Error('Failed to save');
      }
    } catch (error) {
      toast.error('Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async () => {
    try {
      const response = await fetch(`${API}/api/admin/ads/config/toggle`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setConfig((prev) => ({ ...prev, enabled: data.enabled }));
        toast.success(data.message);
      }
    } catch (error) {
      toast.error('Failed to toggle Google Ads');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-4xl space-y-6">
        <Skeleton className="h-12 w-64" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl" data-testid="ad-system-config">
      <div className="mb-8">
        <h1 className="text-3xl font-heading font-medium flex items-center gap-3">
          <Settings className="w-8 h-8 text-primary" />
          Ad System Configuration
        </h1>
        <p className="text-muted-foreground mt-2">
          Configure Google Ad Manager and manage ad placements
        </p>
        {lastUpdated && (
          <p className="text-xs text-muted-foreground mt-1">
            Last updated: {new Date(lastUpdated).toLocaleString()}
          </p>
        )}
      </div>

      <Tabs defaultValue="google" className="space-y-6">
        <TabsList>
          <TabsTrigger value="google">
            <Globe className="w-4 h-4 mr-2" />
            Google Ad Manager
          </TabsTrigger>
          <TabsTrigger value="placements">
            <LayoutDashboard className="w-4 h-4 mr-2" />
            Ad Placements
          </TabsTrigger>
          <TabsTrigger value="analytics">
            <BarChart3 className="w-4 h-4 mr-2" />
            Analytics
          </TabsTrigger>
          <TabsTrigger value="help">
            <Info className="w-4 h-4 mr-2" />
            Setup Guide
          </TabsTrigger>
        </TabsList>

        <TabsContent value="google" className="space-y-6">
          <GoogleAdManagerTab
            config={config}
            setConfig={setConfig}
            saving={saving}
            onSave={handleSave}
            onToggle={handleToggle}
          />
        </TabsContent>

        <TabsContent value="placements" className="space-y-6">
          <AdPlacementsTab placements={placements} />
        </TabsContent>

        <TabsContent value="analytics" className="space-y-6">
          <AdAnalyticsTab stats={stats} />
        </TabsContent>

        <TabsContent value="help" className="space-y-6">
          <SetupGuideTab config={config} onCopy={copyToClipboard} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdSystemConfig;
